import React, { useEffect, useState, useRef } from "react"
import moment from "moment"
import { BiLogoInstagramAlt, BiLogoTiktok } from "react-icons/bi"
import { IoStatsChart, IoDownload } from "react-icons/io5"
import { MdOutlinePreview } from "react-icons/md"
import ModalField from "../../../components/ModalField"
import { TAgetApplications, TAupdateApplicationMetrics } from "../../../services/applicationAPI"
import Pagination from "./components/Pagination"
import Filters from "./components/Filter"
import Sorting from "./components/Sorting"
import CategoryModal from "../../Campaign/allCampaigns/components/CategoryModal"
import { Link, useNavigate } from "react-router-dom"
import { getErrorMessage, getErrorStatus, isAbortError } from "../../../types/error"

const ApplicationDone = () => {
  const navigate = useNavigate()
  const abortControllerRef = useRef<AbortController | null>(null)
  const [isLoading, setIsloading] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(1)
  const [applications, setApplications] = useState<any[]>([])
  const [selectedItemPlatform, setSelectedItemPLatform] = useState("insta-story")
  const [imageData, setImageData] = useState(null)
  const [modalOpen, setModalOpen] = useState(false)
  const [modalType, setModalType] = useState<string>("")
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [sortBy, setSortBy] = useState("")
  const [sortOrder, setSortOrder] = useState(true)
  const [isSortingOpen, setIsSortingOpen] = useState(false)
  const [filters, setFilters] = useState<any>({})
  const [isClassificationOpen, setIsClassificationOpen] = useState(false)
  const [classification, setClassification] = useState("")
  const [error, setError] = useState("")
  const [postUrls, setPostUrls] = useState<Record<string, string>>({})
  const [updatingMetrics, setUpdatingMetrics] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (applications.length > 0) {
      const initialUrls: Record<string, string> = {}
      applications.forEach((item: any) => {
        const postUrl = item.post_url
        if (postUrl) {
          initialUrls[item._id] = postUrl
        }
      })
      setPostUrls((prev) => ({ ...prev, ...initialUrls }))
    }
  }, [applications])

  useEffect(() => {
    getApplications()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, sortBy, sortOrder, filters])

  useEffect(() => {
    setPage(1)
  }, [sortBy, sortOrder, filters])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const getApplications = async () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }

    abortControllerRef.current = new AbortController()

    setError("")
    setIsloading(true)
    try {
      const filteredParams = Object.fromEntries(
        Object.entries(filters).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined,
        ),
      ) as Record<string, string>
      filteredParams.application_date = "2024-01-01"

      const params = new URLSearchParams(filteredParams).toString()
      const response = await TAgetApplications(
        "application_done",
        page,
        perPage,
        sortBy,
        sortOrder,
        params,
        abortControllerRef.current.signal,
      )
      if (response) {
        setApplications(response.applications)
        setTotalItems(response.totalDocuments)
        setIsloading(false)
      }
    } catch (error: unknown) {
      if (isAbortError(error)) {
        setIsloading(false)
        return
      }

      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setIsloading(false)
      setError(getErrorMessage(error) || "Error fetching data")
    }
  }

  const convertToCSV = (data: any[]): string => {
    const headers = [
      "_id",
      "name",
      "campaign",
      "user",
      "age",
      "gender",
      "country",
      "city",
      "application_date",
      "birthday",
      "currency",
      "price_brand",
      "price_user",
      "platform",
      "state",
      "is_banned",
      "insta_followers",
      "insta_following",
      "insta_average_like",
      "insta_average_reels_views",
      "insta_engagement_rate",
      "insta_reels_engagement_rate",
      "tiktok_followers",
      "tiktok_following",
      "tiktok_average_like",
      "tiktok_hearts",
      "tiktok_engagement_rate",
      "hobbies",
    ]

    const rows = data.map((rec) => [
      rec._id,
      rec.name,
      rec.campaign,
      rec.user,
      rec.age,
      rec.gender,
      rec.country,
      rec.city,
      new Date(rec.application_date).toISOString(),
      new Date(rec.birthday).toISOString(),
      rec.currency,
      rec.price_brand,
      rec.price_user,
      rec.platform,
      rec.state,
      rec.is_banned,
      rec.insta_followers,
      rec.insta_following,
      rec.insta_average_like,
      rec.insta_average_reels_views,
      rec.insta_engagement_rate?.toFixed(2),
      rec.insta_reels_engagement_rate?.toFixed(2),
      rec.tiktok_followers,
      rec.tiktok_following,
      rec.tiktok_average_like?.toFixed(2),
      rec.tiktok_hearts,
      rec.tiktok_engagement_rate?.toFixed(2),
      (rec.hobbies || []).join(";"),
    ])

    const escapeCell = (cell: any) => {
      if (cell === null || cell === undefined) {
        return ""
      }
      const s = String(cell)
      if (/[",\n]/.test(s)) {
        return `"${s.replace(/"/g, "''")}"`
      }
      return s
    }

    const csv = [headers, ...rows].map((row) => row.map(escapeCell).join(",")).join("\r\n")

    return csv
  }

  const downloadCSV = (applications: any[]) => {
    const csvString = convertToCSV(applications)
    const blob = new Blob([csvString], { type: "text/csv;charset=utf-8;" })
    const url = window.URL.createObjectURL(blob)

    const link = document.createElement("a")
    link.href = url
    link.setAttribute("download", "application_done.csv")
    link.style.display = "none"
    document.body.appendChild(link)
    link.click()

    document.body.removeChild(link)
    window.URL.revokeObjectURL(url)
  }

  const downloadImage = async (imgUrl: string, id: string) => {
    setDownloadingId(id)
    const res = await fetch(imgUrl, { mode: "cors" })
    const blob = await res.blob()
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = imgUrl.split("/").pop() || "download"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    setDownloadingId(null)
  }

  const onClose = () => {
    setModalOpen(false)
  }

  const handleUpdateMetrics = async (applicationId: string, item?: any) => {
    const postUrl = postUrls[applicationId] || item?.post_url || ""
    if (!postUrl || !postUrl.trim()) {
      setError("Lütfen bir post URL girin")
      return
    }

    setUpdatingMetrics((prev) => ({ ...prev, [applicationId]: true }))
    setError("")
    try {
      await TAupdateApplicationMetrics(applicationId, postUrl.trim())
      await getApplications()
      setPostUrls((prev) => {
        const newUrls = { ...prev }
        delete newUrls[applicationId]
        return newUrls
      })
    } catch (error: unknown) {
      if (getErrorStatus(error) === 401) {
        navigate("/login")
      }
      setError(getErrorMessage(error) || "İstatistik güncellenirken hata oluştu")
    } finally {
      setUpdatingMetrics((prev) => ({ ...prev, [applicationId]: false }))
    }
  }

  return (
    <div className="mt-10">
      {error && <div className="text-red-500 text-center">{error}</div>}
      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-x-12">
          <div>
            <p className="font-poppins-regular text-sm text-[#444449]">Tamamlanan Başvurular</p>
            <p className="font-bold text-lg text-black">{totalItems || 0}</p>
          </div>
        </div>
        <div className="flex flex-row gap-x-4">
          <div
            className="flex w-1/10 bg-gray-100 ml-4 items-center border border-gray-300 rounded-3xl mb-4 p-2 cursor-pointer"
            onClick={() => setIsFilterOpen(true)}
          >
            Filtrele
          </div>
          <button
            className="flex w-1/10 bg-gray-100 ml-4 items-center border border-gray-300 rounded-3xl mb-4 p-2 cursor-pointer"
            onClick={() => setIsSortingOpen(true)}
          >
            Sırala
          </button>
          <div
            className="flex w-1/10 bg-gray-100 ml-4 items-center border border-gray-300 rounded-3xl mb-4 p-2 cursor-pointer"
            onClick={() => setIsClassificationOpen(true)}
          >
            {classification ? classification : "Sınıflandırma Filtresi"}
          </div>
        </div>
        <div>
          <button
            className="btn ml-3 flex items-center gap-1 bg-green-500 text-white rounded-lg p-2"
            onClick={() => downloadCSV(applications)}
          >
            <span className="text-xs">CSV olarak indir</span>
            <i className="fas fa-paper-plane fa-xs"></i>
          </button>
        </div>
      </div>

      <Filters
        isOpen={isFilterOpen}
        onClose={() => setIsFilterOpen(false)}
        handleFetchData={getApplications}
        filters={filters}
        setFilters={setFilters}
      />
      <Sorting
        isOpen={isSortingOpen}
        onClose={() => setIsSortingOpen(false)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        setSortBy={setSortBy}
        setSortOrder={setSortOrder}
      />
      <CategoryModal
        title="Kampanyaları Filtrele"
        isOpen={isClassificationOpen}
        onClose={() => setIsClassificationOpen(false)}
        onSelect={(classification) => {
          setFilters({ ...filters, classification: classification })
          setClassification(classification)
          setIsClassificationOpen(false)
        }}
        selectedValue={{ category: classification }}
      />
      {isLoading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-t-4 border-pink-600"></div>
        </div>
      ) : (
        <div className="w-full grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
          {applications.map((item: any, index: number) => {
            let parsedPayload = item?.parsedPayload || {}
            let imgPayload: any = {}
            try {
              const statePayload = item?.state_history?.[2]?.state_payload || "{}"
              imgPayload = JSON.parse(statePayload)
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error("Error parsing state_payload:", error)
              imgPayload = {}
            }
            let mediaUrl = imgPayload?.img
            return (
              <div
                className="w-full h-96 flex flex-row items-stretch border border-[#E3E3E4] rounded-xl overflow-hidden"
                key={index}
              >
                {mediaUrl?.includes("jpg") ||
                mediaUrl?.includes("jpeg") ||
                mediaUrl?.includes("png") ||
                mediaUrl?.includes("webp") ||
                !mediaUrl?.includes("mp4") ? (
                  <div className="w-2/5 shrink-0 relative group h-full">
                    <img
                      alt="insta_payload_img"
                      src={mediaUrl}
                      className="w-full h-full object-contain shrink-0 transition-opacity duration-300 group-hover:opacity-100"
                    />

                    <div className="w-16 h-8 border-[#F1F1F2]  cursor-pointer bg-[#F1F1F2] rounded-md absolute bottom-5 right-5 flex items-center justify-center z-10">
                      {item.platform?.includes("insta")
                        ? React.createElement(BiLogoInstagramAlt as any, {
                            onClick: () =>
                              window.open(`https://www.instagram.com/${item?.insta_username}`),
                            size: 18,
                            className: "w-1/2 cursor-pointer",
                          })
                        : React.createElement(BiLogoTiktok as any, {
                            onClick: () =>
                              window.open(`https://www.tiktok.com/@${item?.tiktok_username}`),
                            size: 18,
                            className: "w-1/2 cursor-pointer",
                          })}
                      {React.createElement(IoStatsChart as any, {
                        onClick: () => {
                          setImageData(item)
                          setSelectedItemPLatform(item.platform)
                          setModalOpen(true)
                          setModalType("stats")
                        },
                        size: 16,
                        className: "w-1/2 cursor-pointer",
                      })}
                      {downloadingId === item._id ? (
                        <div className="flex items-center justify-center w-full h-full">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-4 border-pink-600"></div>
                        </div>
                      ) : (
                        React.createElement(IoDownload as any, {
                          onClick: async () => {
                            if (mediaUrl) {
                              downloadImage(mediaUrl, item._id)
                            }
                          },
                          size: 16,
                          className: "w-1/2 cursor-pointer",
                        })
                      )}
                    </div>

                    <div className="opacity-0 group-hover:opacity-100  absolute inset-0 flex items-center justify-center bg-gray-400 bg-opacity-50 font-semibold">
                      <button
                        onClick={() => {
                          setImageData(item)
                          setSelectedItemPLatform(item.platform)
                          setModalOpen(true)
                        }}
                        className="flex items-center justify-center gap-2 p-2 rounded-2xl backdrop-blur-2xl text-white "
                      >
                        {React.createElement(MdOutlinePreview as any, { size: 18 })} Önizleme
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-2/5 shrink-0 relative group h-full">
                    <video
                      src={mediaUrl}
                      controls
                      muted
                      loop
                      className="w-full h-full object-contain"
                    />
                    <div className="w-16 h-8 border-[#F1F1F2]  cursor-pointer bg-[#F1F1F2] rounded-md absolute bottom-5 right-5 flex items-center justify-center z-10">
                      {item.platform?.includes("insta")
                        ? React.createElement(BiLogoInstagramAlt as any, {
                            onClick: () =>
                              window.open(`https://www.instagram.com/${item?.insta_username}`),
                            size: 18,
                            className: "w-1/2 cursor-pointer",
                          })
                        : React.createElement(BiLogoTiktok as any, {
                            onClick: () =>
                              window.open(`https://www.tiktok.com/@${item?.tiktok_username}`),
                            size: 18,
                            className: "w-1/2 cursor-pointer",
                          })}
                      {React.createElement(IoStatsChart as any, {
                        onClick: () => {
                          setImageData(item)
                          setSelectedItemPLatform(item.platform)
                          setModalOpen(true)
                          setModalType("stats")
                        },
                        size: 16,
                        className: "w-1/2 cursor-pointer",
                      })}
                      {downloadingId === item._id ? (
                        <div className="flex items-center justify-center w-full h-full">
                          <div className="animate-spin rounded-full h-6 w-6 border-t-4 border-pink-600"></div>
                        </div>
                      ) : (
                        React.createElement(IoDownload as any, {
                          onClick: async () => {
                            if (mediaUrl) {
                              downloadImage(mediaUrl, item._id)
                            }
                          },
                          size: 16,
                          className: "w-1/2 cursor-pointer",
                        })
                      )}
                    </div>
                  </div>
                )}
                <div className="flex flex-col flex-1 min-w-0 h-96 pt-3 items-start overflow-hidden">
                  <div className="flex flex-col w-full px-3 h-full overflow-y-auto min-h-0">
                    {/* User Profile Section */}
                    <div className="flex flex-row gap-x-2 mb-2">
                      <img
                        alt="profile_picture"
                        className="h-10 w-10 shrink-0 object-contain rounded-md"
                        src={
                          item.platform?.includes("insta")
                            ? item?.insta_profile_pic
                            : item?.tiktok_profile_pic
                        }
                      />
                      <div className="flex flex-col gap-x-1 items-start">
                        <p className="font-semibold text-base text-black">
                          @
                          {item.platform?.includes("insta")
                            ? item?.insta_username
                            : item?.tiktok_username}
                        </p>
                        <p className="font-semibold text-xs text-[#5A5A62]">
                          {item.platform?.includes("insta")
                            ? item?.insta_full_name
                            : item?.tiktok_nickname}
                        </p>
                      </div>
                    </div>

                    {/* Basic Stats */}
                    <div className="flex flex-row gap-x-2 mb-1">
                      <div className="flex-1">
                        <p className="font-poppins-regular text-xs text-[#71717A]">
                          Takipçi Sayısı
                        </p>
                        <p className="font-semibold text-sm text-black">
                          {item.platform?.includes("insta")
                            ? item?.insta_followers?.toLocaleString("tr-TR")
                            : item?.tiktok_followers?.toLocaleString("tr-TR")}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="font-poppins-regular text-xs text-[#71717A]">
                          Etkileşim Oranı
                        </p>
                        <p className="font-semibold text-sm text-black">
                          {item.platform?.includes("insta")
                            ? item?.insta_engagement_rate?.toFixed(1)
                            : item?.tiktok_engagement_rate?.toFixed(1)}
                          %
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="font-poppins-regular text-xs text-[#71717A]">Tıklanma</p>
                        <p className="font-semibold text-sm text-black">{item.clicks || 0}</p>
                      </div>
                      <div className="flex-1">
                        <p className="font-poppins-regular text-xs text-[#71717A]">
                          Tıklanma/Tutar
                        </p>
                        <p className="font-semibold text-sm text-black">
                          {(item.clicksToPriceRatio ?? 0).toFixed(2)}
                        </p>
                      </div>
                      <div className="flex-1">
                        <p className="font-poppins-regular text-xs text-[#71717A]">
                          Tıklanma/Tutar
                        </p>
                        <p className="font-semibold text-sm text-black">
                          {(item.viewToPriceRatio ?? 0).toFixed(2)}
                        </p>
                      </div>
                    </div>

                    {/* ParsedPayload Stats - Instagram */}
                    {item.platform?.includes("insta") && (
                      <div className="flex flex-col gap-1 mb-1">
                        <div className="grid grid-cols-3 gap-1">
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">İzlenme</p>
                            <p className="font-semibold text-xs text-black">
                              {parsedPayload?.views || parsedPayload?.view || 0}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Beğeni</p>
                            <p className="font-semibold text-xs text-black">
                              {parsedPayload?.likes || parsedPayload?.like || 0}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Yorum</p>
                            <p className="font-semibold text-xs text-black">
                              {parsedPayload?.comments || parsedPayload?.comment || 0}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Paylaşım</p>
                            <p className="font-semibold text-xs text-black">
                              {parsedPayload?.shares || parsedPayload?.share || 0}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Kaydetme</p>
                            <p className="font-semibold text-xs text-black">
                              {parsedPayload?.savings || parsedPayload?.saving || 0}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Etkileşim</p>
                            <p className="font-semibold text-xs text-black">
                              {parsedPayload?.interactions || parsedPayload?.interaction || 0}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ParsedPayload Stats - TikTok */}
                    {item.platform?.includes("tiktok") && (
                      <div className="flex flex-col gap-1 mb-1">
                        <div className="grid grid-cols-3 gap-1">
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">İzlenme</p>
                            <p className="font-semibold text-xs text-black">
                              {Number(
                                parsedPayload?.play_count ||
                                  parsedPayload?.views ||
                                  parsedPayload?.view ||
                                  0,
                              ).toLocaleString("tr-TR")}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Beğeni</p>
                            <p className="font-semibold text-xs text-black">
                              {Number(
                                parsedPayload?.like_count ||
                                  parsedPayload?.likes ||
                                  parsedPayload?.like ||
                                  0,
                              ).toLocaleString("tr-TR")}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Yorum</p>
                            <p className="font-semibold text-xs text-black">
                              {Number(
                                parsedPayload?.comment_count ||
                                  parsedPayload?.comments ||
                                  parsedPayload?.comment ||
                                  0,
                              ).toLocaleString("tr-TR")}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Paylaşım</p>
                            <p className="font-semibold text-xs text-black">
                              {Number(
                                parsedPayload?.share_count ||
                                  parsedPayload?.shares ||
                                  parsedPayload?.share ||
                                  0,
                              ).toLocaleString("tr-TR")}
                            </p>
                          </div>
                          <div>
                            <p className="font-poppins-regular text-xs text-[#71717A]">Kalp</p>
                            <p className="font-semibold text-xs text-black">
                              {Number(
                                parsedPayload?.hearts || parsedPayload?.heart || 0,
                              ).toLocaleString("tr-TR")}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* ParsedPayload Stats - UGC or other platforms */}
                    {!item.platform?.includes("insta") &&
                      !item.platform?.includes("tiktok") &&
                      (parsedPayload?.view ||
                        parsedPayload?.views ||
                        parsedPayload?.like ||
                        parsedPayload?.likes ||
                        parsedPayload?.comment ||
                        parsedPayload?.comments ||
                        parsedPayload?.share ||
                        parsedPayload?.shares) && (
                        <div className="flex flex-col gap-1 mb-1">
                          <div className="grid grid-cols-3 gap-1">
                            <div>
                              <p className="font-poppins-regular text-xs text-[#71717A]">İzlenme</p>
                              <p className="font-semibold text-xs text-black">
                                {Number(
                                  parsedPayload?.play_count ||
                                    parsedPayload?.views ||
                                    parsedPayload?.view ||
                                    0,
                                ).toLocaleString("tr-TR")}
                              </p>
                            </div>
                            <div>
                              <p className="font-poppins-regular text-xs text-[#71717A]">Beğeni</p>
                              <p className="font-semibold text-xs text-black">
                                {Number(
                                  parsedPayload?.like_count ||
                                    parsedPayload?.likes ||
                                    parsedPayload?.like ||
                                    0,
                                ).toLocaleString("tr-TR")}
                              </p>
                            </div>
                            <div>
                              <p className="font-poppins-regular text-xs text-[#71717A]">Yorum</p>
                              <p className="font-semibold text-xs text-black">
                                {Number(
                                  parsedPayload?.comment_count ||
                                    parsedPayload?.comments ||
                                    parsedPayload?.comment ||
                                    0,
                                ).toLocaleString("tr-TR")}
                              </p>
                            </div>
                            <div>
                              <p className="font-poppins-regular text-xs text-[#71717A]">
                                Paylaşım
                              </p>
                              <p className="font-semibold text-xs text-black">
                                {Number(
                                  parsedPayload?.share_count ||
                                    parsedPayload?.shares ||
                                    parsedPayload?.share ||
                                    0,
                                ).toLocaleString("tr-TR")}
                              </p>
                            </div>
                            {parsedPayload?.savings !== undefined && (
                              <div>
                                <p className="font-poppins-regular text-xs text-[#71717A]">
                                  Kaydetme
                                </p>
                                <p className="font-semibold text-xs text-black">
                                  {Number(
                                    parsedPayload?.savings || parsedPayload?.saving || 0,
                                  ).toLocaleString("tr-TR")}
                                </p>
                              </div>
                            )}
                            {parsedPayload?.interactions !== undefined && (
                              <div>
                                <p className="font-poppins-regular text-xs text-[#71717A]">
                                  Etkileşim
                                </p>
                                <p className="font-semibold text-xs text-black">
                                  {Number(
                                    parsedPayload?.interactions || parsedPayload?.interaction || 0,
                                  ).toLocaleString("tr-TR")}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                    {/* Campaign Info */}
                    <div className="flex flex-col gap-1 mb-1">
                      <div className="flex flex-row items-center justify-between">
                        <p className="font-poppins-regular text-xs text-[#71717A]">Son Aktif</p>
                        <p className="font-semibold text-sm text-black">
                          {moment(item?.last_action_at).format("DD.MM.YYYY")}
                        </p>
                      </div>
                      <div className="flex flex-row items-center justify-between">
                        <p className="font-poppins-regular text-xs text-[#71717A]">Tutar</p>
                        <p className="font-semibold text-sm text-black">
                          {item?.price_brand} {item?.currency}
                        </p>
                      </div>
                      <div className="flex flex-row items-start justify-between">
                        <p className="font-poppins-regular text-xs text-[#71717A] mt-0.5">
                          Kampanya
                        </p>
                        <Link
                          to={`/campaigns/find/${item?.campaign}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <p
                            className="font-semibold text-sm text-blue-600 hover:text-blue-800 cursor-pointer text-right"
                            title="Kampanya profiline git"
                          >
                            {item?.name?.length > 30 ? item.name.slice(0, 30) + "..." : item?.name}
                          </p>
                        </Link>
                      </div>
                      <div className="flex flex-row items-start justify-between">
                        <p className="font-poppins-regular text-xs text-[#71717A] mt-0.5">Marka</p>
                        <Link
                          to={`/brands/find/${item?.brandId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <p
                            className="font-semibold text-sm text-green-600 hover:text-green-800 cursor-pointer text-right"
                            title="Marka profiline git"
                          >
                            {item?.brandName?.length > 30
                              ? item.brandName.slice(0, 30) + "..."
                              : item?.brandName}
                          </p>
                        </Link>
                      </div>
                      <div className="flex flex-row items-center justify-between">
                        <p className="font-poppins-regular text-xs text-[#71717A]">Platform</p>
                        <p className="font-semibold text-sm text-black">{item?.platform || ""}</p>
                      </div>
                    </div>

                    {/* Post URL Input and Update Metrics Section */}
                    <div className="flex flex-col gap-2 mt-2 pb-2 border-t border-gray-200 pt-2">
                      <div className="flex flex-col gap-1">
                        <label className="font-poppins-regular text-xs text-[#71717A]">
                          Post URL
                        </label>
                        <input
                          type="text"
                          value={postUrls[item._id] || item.post_url || ""}
                          onChange={(e) =>
                            setPostUrls((prev) => ({ ...prev, [item._id]: e.target.value }))
                          }
                          placeholder="Instagram veya TikTok post URL'si girin"
                          className="w-full px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      {(postUrls[item._id] || item.post_url) && (
                        <button
                          onClick={() => handleUpdateMetrics(item._id, item)}
                          disabled={updatingMetrics[item._id]}
                          className="w-full px-3 py-1.5 text-xs font-semibold text-white bg-blue-500 hover:bg-blue-600 disabled:bg-gray-400 disabled:cursor-not-allowed rounded-md transition-colors flex items-center justify-center gap-1"
                        >
                          {updatingMetrics[item._id] ? (
                            <>
                              <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-white border-r-2"></div>
                              <span>Güncelleniyor...</span>
                            </>
                          ) : (
                            <span>İstatistik Güncelle</span>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
          {imageData && (
            <ModalField
              modalOpen={modalOpen}
              onClose={onClose}
              data={imageData}
              platform={selectedItemPlatform}
              modalType={modalType}
            />
          )}
        </div>
      )}
      <Pagination
        page={page}
        setPage={setPage}
        totalItems={totalItems}
        itemsPerPage={perPage}
        setItemsPerPage={setPerPage}
      />
    </div>
  )
}

export default ApplicationDone
